import React from 'react';
import { motion } from 'motion/react';
import SEO from '@/components/SEO';
import { BackgroundAnimation } from '@/components/background-animation';
import { ArrowRight, BookOpen, Clock, BarChart, HelpCircle, FileText, Bell, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import { TUTORIALS } from '@/data/tutorials';
import { DiscordCTA } from '@/components/DiscordCTA';
import InternalLinksSection from '@/components/InternalLinksSection';

const TutorialsHub: React.FC = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="Creator Tutorials | How to Make Satisfying Physics Videos"
        description="Master the Fun Circle Games Creator Studio with our step-by-step tutorials. Learn to make viral ball simulations, ASMR loops, and rhythmic animations."
        keywords="physics simulation tutorial, make satisfying videos, ball simulator guide, asmr video creation, faceless channel tips"
        canonical="/tutorials"
      />
      <BackgroundAnimation />

      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-20"
          >
             <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-50 border border-cyan-100 mb-8 font-sans text-[10px] uppercase tracking-[0.3em] text-cyan-600 mx-auto">
              <BookOpen className="w-3.5 h-3.5" />
              <span>Academy & Tutorials</span>
            </div>
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold font-display text-zinc-950 mb-8 uppercase tracking-tighter leading-none">
              Creator <span className="text-zinc-400">Tutorials</span>
            </h1>
            <p className="text-zinc-500 text-lg max-w-2xl mx-auto font-sans">
              Learn the techniques used by top creators to generate millions of views with satisfying physics simulations.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {TUTORIALS.map((tutorial, idx) => (
              <motion.div
                key={tutorial.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="group p-10 rounded-[2rem] bg-white border border-zinc-950/10 shadow-sm hover:shadow-xl transition-all"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="px-4 py-1 rounded-full bg-zinc-50 border border-zinc-100 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                    {tutorial.category}
                  </span>
                  <div className="flex items-center gap-4 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                    <div className="flex items-center gap-1.5">
                       <BarChart className="w-3 h-3 text-cyan-500" />
                       {tutorial.level}
                    </div>
                    <div className="flex items-center gap-1.5">
                       <Clock className="w-3 h-3 text-cyan-500" />
                       {tutorial.time}
                    </div>
                  </div>
                </div>
                <h3 className="text-2xl font-bold font-display tracking-tight mb-4 group-hover:text-cyan-600 transition-colors">
                  {tutorial.title}
                </h3>
                <p className="text-zinc-500 mb-8 leading-relaxed">
                  {tutorial.description}
                </p>
                <Link to={`/tutorials/${tutorial.slug}`} className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "text-cyan-600 hover:text-cyan-700 p-0 font-bold uppercase tracking-widest text-[10px] group-hover:translate-x-2 transition-transform")}>
                  Read Tutorial <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-zinc-50 border-t border-zinc-100 text-zinc-950 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-cyan-500/5 pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 relative z-10 space-y-16">
          <div className="space-y-6">
            <h2 className="text-4xl md:text-6xl font-bold font-display text-zinc-950 mb-8 uppercase tracking-tighter">
              Ready to Apply This?
            </h2>
            <p className="text-zinc-500 text-lg mb-12">
              Jump into the Creator Studio and start experimenting with these techniques today.
            </p>
            <Link to="/register" className={cn(buttonVariants({ size: "lg" }), "rounded-full px-12 h-16 bg-cyan-600 text-white hover:bg-cyan-700 transition-all font-bold uppercase tracking-[0.3em] text-xs font-sans shadow-lg shadow-cyan-600/10")}>
              Open Creator Studio
            </Link>
          </div>

          <div className="pt-16 border-t border-zinc-200 text-left space-y-6">
            <h3 className="text-lg font-bold font-display uppercase tracking-tight text-foreground">Explore More Resources</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <Link to="/plans" className="p-4 rounded-2xl bg-white border border-zinc-200 hover:border-cyan-600/30 flex items-center gap-3 group transition-all">
                <div className="w-8 h-8 rounded-xl bg-cyan-50 flex items-center justify-center text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white transition-all">
                  <ArrowRight className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-sm font-bold">Pricing & Plans</h4>
                  <p className="text-xs text-muted-foreground">Unlock higher export resolutions</p>
                </div>
              </Link>
              <Link to="/help" className="p-4 rounded-2xl bg-white border border-zinc-200 hover:border-cyan-600/30 flex items-center gap-3 group transition-all">
                <div className="w-8 h-8 rounded-xl bg-cyan-50 flex items-center justify-center text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white transition-all">
                  <HelpCircle className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-sm font-bold">Help Center</h4>
                  <p className="text-xs text-muted-foreground">100+ feature guides and FAQs</p>
                </div>
              </Link>
              <Link to="/blog" className="p-4 rounded-2xl bg-white border border-zinc-200 hover:border-cyan-600/30 flex items-center gap-3 group transition-all">
                <div className="w-8 h-8 rounded-xl bg-cyan-50 flex items-center justify-center text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white transition-all">
                  <FileText className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-sm font-bold">Creator Blog</h4>
                  <p className="text-xs text-muted-foreground">Insights, trends & growth tips</p>
                </div>
              </Link>
              <Link to="/showcase" className="p-4 rounded-2xl bg-white border border-zinc-200 hover:border-cyan-600/30 flex items-center gap-3 group transition-all">
                <div className="w-8 h-8 rounded-xl bg-cyan-50 flex items-center justify-center text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white transition-all">
                  <BookOpen className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-sm font-bold">Showcase</h4>
                  <p className="text-xs text-muted-foreground">See outstanding creator work</p>
                </div>
              </Link>
              <Link to="/updates" className="p-4 rounded-2xl bg-white border border-zinc-200 hover:border-cyan-600/30 flex items-center gap-3 group transition-all">
                <div className="w-8 h-8 rounded-xl bg-cyan-50 flex items-center justify-center text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white transition-all">
                  <Bell className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-sm font-bold">Product Updates</h4>
                  <p className="text-xs text-muted-foreground">Recent changes and fixes</p>
                </div>
              </Link>
              <a href="https://discord.gg/cdwNFdbx" target="_blank" rel="noopener noreferrer" className="p-4 rounded-2xl bg-[#5865F2]/5 border border-[#5865F2]/10 hover:border-[#5865F2]/30 flex items-center gap-3 group transition-all">
                <div className="w-8 h-8 rounded-xl bg-[#5865F2]/10 flex items-center justify-center text-[#5865F2] group-hover:bg-[#5865F2] group-hover:text-white transition-all">
                  <MessageSquare className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-[#5865F2]">Discord Community</h4>
                  <p className="text-xs text-muted-foreground">Chat with fellow video creators</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      <DiscordCTA variant="banner" />

      <InternalLinksSection />
    </div>
  );
};

export default TutorialsHub;
